/* leafLabels.js — THE LABEL COMES FROM ITS OWNER, NOT FROM A CACHE.
 *
 * `gl_line_mappings.display_name` is the operator's own name for a GL line and the ONLY
 * writer of it is the operator (the Step-3 grid and the Chart-of-Accounts rename). Two
 * other tables carry a COPY of that string, written by the onboarding import:
 *
 *     defrev_data.bs_actuals_meta[<leafKey>].label     — read by buildBSLines / buildCFSLines
 *     revenue_module_mappings.display_name             — read by buildISLines' dynRevLeaves
 *
 * A copy with two writers drifts, and both of these measurably have (see
 * diffs/label-cache-phase0.txt). This module makes the copies stop being the SOURCE: the
 * owner's value is overlaid onto the cached object immediately before the builders read
 * it, so a rename shows up on the statements with no cache to refresh.
 *
 * ⛔ MATCHED BY KEY, NEVER BY NAME. The glm row is found by its stamped
 *   `leaf_key ?? is_line_key` (the pinned key, b710c10). A label lookup keyed on the NAME
 *   would break during a rename — the one moment it has to work — and name-matching is the
 *   defect the whole rename arc exists to kill.
 *
 * ⓘ THE FALLBACK IS LOAD-BEARING, NOT DEFENSIVE. A leaf with no owning glm row keeps its
 *   cached label. That is the real mid-wizard state — the Step-4b review screen renders
 *   from this same meta object before the model exists — and it is why the cache columns
 *   are left in place rather than dropped.
 */

/** Index gl_line_mappings rows by the leaf key they own. First row wins (the glm dedupe
 *  elsewhere is keyed on id order; a duplicate key is a P2 offender, not a tie to break). */
export function indexGlByLeafKey(glMappings) {
    const byKey = new Map();
    for (const g of (glMappings || [])) {
        const k = g && (g.leaf_key || g.is_line_key);
        if (k && !byKey.has(k)) byKey.set(k, g);
    }
    return byKey;
}

/** The owner's label for a leaf key, or null when no glm row owns it. */
export function ownedLabelFor(byKey, leafKey) {
    const g = byKey.get(leafKey);
    if (!g) return null;
    return g.display_name || g.gl_name || null;
}

/** Return a bs_actuals_meta object whose `label`s come from gl_line_mappings.
 *  Returns the SAME object when nothing moves, so a no-op costs no allocation and a
 *  reference comparison still means "unchanged". */
export function overlayLeafLabels(bsActualsMeta, glMappings) {
    const meta = bsActualsMeta || {};
    const keys = Object.keys(meta);
    if (!keys.length || !(glMappings || []).length) return meta;
    const byKey = indexGlByLeafKey(glMappings);
    let moved = 0;
    const out = {};
    for (const k of keys) {
        const leaf = meta[k];
        const owned = ownedLabelFor(byKey, k);
        if (leaf && owned && owned !== leaf.label) { out[k] = { ...leaf, label: owned }; moved++; }
        else out[k] = leaf;
    }
    return moved ? out : meta;
}

/* ★★ THE OPERATOR-FACING NAME OF A REVENUE MODULE.
 *
 * THE RULE THIS FOLLOWS, and it is the one the 09-09 census landed on: **the engine name
 * stays wherever the text describes THE ENGINE (a module, a settings row, a code path, a
 * diagnostic) and changes only where it describes THIS TENANT'S REVENUE (a tab, a chart
 * series, a statement row).** An operator does not know 'AE Quota'; they know the income
 * statement line it forecasts.
 *
 * ⛔ THE SOURCE IS THE OWNER, NOT A NICKNAME AND NOT A CACHE.
 *   `revenue_module_mappings.module_name` is an operator-set NICKNAME and
 *   `revenue_module_mappings.display_name` is a CACHE of the owner. MEASURED 2026-09-09 on
 *   all four tenants: the nickname reads "Enterprise" where the line reads "Enterprise
 *   Revenue" — and because the Chart-of-Accounts rename endpoint (3052562) writes ONLY
 *   `gl_line_mappings.display_name`, neither the nickname nor the cache follows a rename.
 *   A tab built on either would show the old name and read as a fresh defect.
 *   ⇒ this resolves through `ownedLabelFor`, the same owner lookup the statements use.
 *
 * ⓘ UNMAPPED RETURNS THE ENGINE NAME, DELIBERATELY (Brian, 09-09). Before a module is
 *   wired there is no line to name, and onboarding is run by a trained operator.
 */
export function moduleLineLabel(glMappings, revMappings, moduleType, engineName) {
    const row = (revMappings || []).find(m => m && m.module_type === moduleType);
    if (!row || !row.gl_line_key) return engineName;
    const owned = ownedLabelFor(indexGlByLeafKey(glMappings), row.gl_line_key);
    return owned || engineName;
}
